/**
 * BlazeFace で顔矩形を検出し、矩形内を主にモザイク（任意で軽いグリッチ）で加工する。
 * フル画面 canvas に毎フレーム映像を描いてから顔領域を上書きする。
 */
import * as blazeface from "@tensorflow-models/blazeface";

import { devConfig } from "./appConfig.js";
import {
  noteInferenceDuration,
  noteInferenceFailure,
  noteInferenceSuccess,
} from "./liveHealth.js";
import { ensureTfBackend } from "./tfBackend.js";
import { drawVideoCover } from "./personDetection.js";

/** @type {HTMLVideoElement | null} */
let privacySourceVideo = null;

/** @type {Promise<import("@tensorflow-models/blazeface").BlazeFaceModel | null> | null} */
let modelPromise = null;
/** @type {import("@tensorflow-models/blazeface").BlazeFaceModel | null} */
let blazeRef = null;

/**
 * @returns {Promise<import("@tensorflow-models/blazeface").BlazeFaceModel | null>}
 */
export async function ensureFacePrivacyModel() {
  const cfg = devConfig.facePrivacy;
  if (!cfg.enabled) return null;

  await ensureTfBackend();
  if (!modelPromise) {
    modelPromise = (async () => {
      try {
        const m = await blazeface.load({
          maxFaces: cfg.maxFaces,
          scoreThreshold: cfg.scoreThreshold,
        });
        blazeRef = m;
        return m;
      } catch (e) {
        console.error(e);
        return null;
      }
    })();
  }
  return modelPromise;
}

/** @type {boolean} */
let running = false;
/** @type {number} */
let rafId = 0;
/** @type {HTMLCanvasElement | null} */
let workCanvas = null;
/** @type {CanvasRenderingContext2D | null} */
let workCtx = null;
/** @type {HTMLCanvasElement | null} */
let patchCanvas = null;
/** @type {CanvasRenderingContext2D | null} */
let patchCtx = null;

/** @type {{ x: number; y: number; w: number; h: number }[]} */
let cachedRects = [];

/** @type {number} */
let lastInferAt = 0;
/** @type {boolean} */
let inferBusy = false;

/**
 * @returns {{ dw: number; dh: number; iw: number; ih: number }}
 */
function detectSize() {
  const iw = window.innerWidth;
  const ih = window.innerHeight;
  let dw = iw;
  let dh = ih;
  const maxSideLimit = devConfig.facePrivacy.maxSidePx;
  const maxSide = Math.max(dw, dh);
  if (maxSide > maxSideLimit) {
    const s = maxSideLimit / maxSide;
    dw = Math.round(dw * s);
    dh = Math.round(dh * s);
  }
  return { dw, dh, iw, ih };
}

/**
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @param {number} padRatio
 * @param {number} maxW
 * @param {number} maxH
 */
function padBox(x1, y1, x2, y2, padRatio, maxW, maxH) {
  let x = Math.min(x1, x2);
  let y = Math.min(y1, y2);
  let w = Math.abs(x2 - x1);
  let h = Math.abs(y2 - y1);
  const px = w * padRatio;
  const py = h * padRatio;
  x -= px * 0.5;
  y -= py * 0.5;
  w += px;
  h += py;
  x = Math.max(0, x);
  y = Math.max(0, y);
  w = Math.min(w, maxW - x);
  h = Math.min(h, maxH - y);
  if (w < 2 || h < 2) return null;
  return { x, y, w, h };
}

/**
 * 検出矩形を重心から拡大（エフェクト範囲を一回り広げる）
 * @param {{ x: number; y: number; w: number; h: number }} r 画面座標
 * @param {number} factor
 * @param {number} iw
 * @param {number} ih
 */
function expandScreenRect(r, factor, iw, ih) {
  if (!factor || factor === 1) return r;
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  let w = r.w * factor;
  let h = r.h * factor;
  let x = cx - w / 2;
  let y = cy - h / 2;
  x = Math.max(0, x);
  y = Math.max(0, y);
  if (x + w > iw) w = iw - x;
  if (y + h > ih) h = ih - y;
  return { x, y, w: Math.max(2, w), h: Math.max(2, h) };
}

/**
 * @param {HTMLVideoElement} video
 * @param {{ x: number; y: number; w: number; h: number }} r
 * @param {number} iw
 * @param {number} ih
 */
function screenRectToVideoCrop(video, r, iw, ih) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  const scale = Math.max(iw / vw, ih / vh);
  const visW = iw / scale;
  const visH = ih / scale;
  const offX = (vw - visW) / 2;
  const offY = (vh - visH) / 2;
  return {
    sx: offX + r.x / scale,
    sy: offY + r.y / scale,
    sw: r.w / scale,
    sh: r.h / scale,
  };
}

/**
 * @param {Uint8ClampedArray} data
 * @param {number} w
 * @param {number} h
 * @param {number} block
 */
function applyMosaic(data, w, h, block) {
  if (block < 2) return;
  for (let y = 0; y < h; y += block) {
    for (let x = 0; x < w; x += block) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      let c = 0;
      for (let dy = 0; dy < block && y + dy < h; dy++) {
        for (let dx = 0; dx < block && x + dx < w; dx++) {
          const i = ((y + dy) * w + (x + dx)) * 4;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          a += data[i + 3];
          c++;
        }
      }
      r /= c;
      g /= c;
      b /= c;
      a /= c;
      for (let dy = 0; dy < block && y + dy < h; dy++) {
        for (let dx = 0; dx < block && x + dx < w; dx++) {
          const i = ((y + dy) * w + (x + dx)) * 4;
          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
          data[i + 3] = a;
        }
      }
    }
  }
}

/**
 * @param {Uint8ClampedArray} data
 * @param {number} w
 * @param {number} h
 * @param {{ tearBands: number; rgbShiftPx: number; noiseDensity: number }} g
 */
function applyGlitch(data, w, h, g) {
  if (g.tearBands <= 0 && g.rgbShiftPx <= 0 && g.noiseDensity <= 0) return;

  const rowBytes = w * 4;
  const bands = g.tearBands;
  for (let b = 0; b < bands; b++) {
    const y0 = (Math.random() * Math.max(1, h - 4)) | 0;
    const bh = 2 + ((Math.random() * 9) | 0);
    const shift = ((Math.random() * 34) | 0) - 17;
    for (let y = y0; y < Math.min(h, y0 + bh); y++) {
      const rs = y * rowBytes;
      const tmp = new Uint8ClampedArray(rowBytes);
      tmp.set(data.subarray(rs, rs + rowBytes));
      for (let x = 0; x < w; x++) {
        const sx = (x + shift + w * 64) % w;
        const di = rs + x * 4;
        const si = rs + sx * 4;
        data[di] = tmp[si];
        data[di + 1] = tmp[si + 1];
        data[di + 2] = tmp[si + 2];
        data[di + 3] = tmp[si + 3];
      }
    }
  }

  if (g.rgbShiftPx > 0) {
    const snap = new Uint8ClampedArray(data);
    const rsPx = g.rgbShiftPx;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        const xr = (x - rsPx + w * 64) % w;
        const xb = (x + rsPx) % w;
        const ir = (y * w + xr) * 4;
        const ib = (y * w + xb) * 4;
        data[i] = snap[ir];
        data[i + 1] = snap[i + 1];
        data[i + 2] = snap[ib + 2];
      }
    }
  }

  const nd = g.noiseDensity;
  if (nd <= 0) return;
  for (let i = 0; i < data.length; i += 4) {
    if (Math.random() < nd) {
      const n = (Math.random() * 255) | 0;
      data[i] = (data[i] ^ n) & 255;
      data[i + 1] = (data[i + 1] ^ (n >> 1)) & 255;
      data[i + 2] = (data[i + 2] ^ (n >> 2)) & 255;
    }
  }
}

/**
 * @param {HTMLCanvasElement} overlay
 */
export function resizeFacePrivacyCanvas(overlay) {
  if (!overlay) return;
  const dpr = Math.min(window.devicePixelRatio ?? 1, 2);
  const iw = window.innerWidth;
  const ih = window.innerHeight;
  overlay.width = Math.floor(iw * dpr);
  overlay.height = Math.floor(ih * dpr);
  overlay.style.width = `${iw}px`;
  overlay.style.height = `${ih}px`;
  const octx = overlay.getContext("2d");
  if (octx) octx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/**
 * @param {{ video: HTMLVideoElement; overlay: HTMLCanvasElement }} opts
 */
export function startFacePrivacy(opts) {
  const { video, overlay } = opts;
  stopFacePrivacy(overlay);

  if (!devConfig.facePrivacy.enabled) return;

  privacySourceVideo = video;
  video.classList.add("camera--privacy-source");

  workCanvas ??= document.createElement("canvas");
  workCtx ??= workCanvas.getContext("2d", { willReadFrequently: true });
  patchCanvas ??= document.createElement("canvas");
  patchCtx ??= patchCanvas.getContext("2d", { willReadFrequently: true });
  if (!workCtx || !patchCtx) return;

  running = true;
  cachedRects = [];
  lastInferAt = 0;
  inferBusy = false;

  resizeFacePrivacyCanvas(overlay);

  async function runInfer() {
    const cfg = devConfig.facePrivacy;
    const intervalMs = cfg.intervalMs ?? 260;
    const t0 = performance.now();
    try {
      const model = await ensureFacePrivacyModel();
      if (!running || !model || !video.srcObject) return;

      const { dw, dh, iw, ih } = detectSize();
      workCanvas.width = dw;
      workCanvas.height = dh;
      workCtx.fillStyle = "#000";
      workCtx.fillRect(0, 0, dw, dh);
      if (!drawVideoCover(workCtx, video, dw, dh)) {
        cachedRects = [];
        return;
      }

      const preds = await model.estimateFaces(
        workCanvas,
        false,
        cfg.flipHorizontal,
        false,
      );

      const sx = iw / dw;
      const sy = ih / dh;
      const padR = cfg.boxPaddingRatio;
      const outset = cfg.boxEffectOutsetScale ?? 1;
      /** @type {{ x: number; y: number; w: number; h: number }[]} */
      const next = [];

      for (const p of preds) {
        const tl = /** @type {[number, number]} */ (p.topLeft);
        const br = /** @type {[number, number]} */ (p.bottomRight);
        const boxed = padBox(tl[0], tl[1], br[0], br[1], padR, dw, dh);
        if (!boxed) continue;
        let r = {
          x: boxed.x * sx,
          y: boxed.y * sy,
          w: boxed.w * sx,
          h: boxed.h * sy,
        };
        r = expandScreenRect(r, outset, iw, ih);
        next.push(r);
      }

      cachedRects = next;
      noteInferenceSuccess();
    } catch (e) {
      console.error(e);
      cachedRects = [];
      noteInferenceFailure();
    } finally {
      inferBusy = false;
      noteInferenceDuration("face", performance.now() - t0, intervalMs);
    }
  }

  function drawLoop() {
    if (!running) return;

    const octx = overlay.getContext("2d");
    if (!octx) return;

    const cfg = devConfig.facePrivacy;
    const iw = window.innerWidth;
    const ih = window.innerHeight;
    const now = performance.now();

    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, overlay.width, overlay.height);
    const dpr = Math.min(window.devicePixelRatio ?? 1, 2);
    octx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (!drawVideoCover(octx, video, iw, ih)) {
      rafId = requestAnimationFrame(drawLoop);
      return;
    }

    if (
      now - lastInferAt >= cfg.intervalMs &&
      !inferBusy &&
      video.readyState >= 2
    ) {
      lastInferAt = now;
      inferBusy = true;
      void runInfer();
    }

    const rects = cachedRects;
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const maxSide = cfg.maxPatchSidePx;
    const moz = cfg.mosaicBlockPx | 0;
    const useGlitch = cfg.useGlitch === true;

    for (const r of rects) {
      const bx = r.x;
      const by = r.y;
      const bw = r.w;
      const bh = r.h;

      let pw = Math.max(2, Math.floor(bw));
      let ph = Math.max(2, Math.floor(bh));
      const long = Math.max(pw, ph);
      let scaleDown = 1;
      if (long > maxSide) {
        scaleDown = maxSide / long;
        pw = Math.max(2, Math.floor(pw * scaleDown));
        ph = Math.max(2, Math.floor(ph * scaleDown));
      }

      const crop = screenRectToVideoCrop(video, r, iw, ih);
      if (!crop) continue;

      patchCanvas.width = pw;
      patchCanvas.height = ph;
      patchCtx.drawImage(
        video,
        crop.sx,
        crop.sy,
        crop.sw,
        crop.sh,
        0,
        0,
        pw,
        ph,
      );

      const img = patchCtx.getImageData(0, 0, pw, ph);

      if (reduced) {
        const mb = cfg.reducedMosaicBlockPx ?? Math.max(14, moz + 4);
        applyMosaic(img.data, pw, ph, mb);
      } else {
        if (moz >= 2) {
          applyMosaic(img.data, pw, ph, moz);
        }
        if (useGlitch) {
          const g = cfg.glitch;
          applyGlitch(img.data, pw, ph, {
            tearBands: g.tearBands,
            rgbShiftPx: g.rgbShiftPx,
            noiseDensity: g.noiseDensity,
          });
        }
      }

      patchCtx.putImageData(img, 0, 0);

      octx.drawImage(patchCanvas, 0, 0, pw, ph, bx, by, bw, bh);
    }

    rafId = requestAnimationFrame(drawLoop);
  }

  drawLoop();
}

/**
 * @param {HTMLCanvasElement | null} [overlay]
 */
export function stopFacePrivacy(overlay) {
  running = false;
  privacySourceVideo?.classList.remove("camera--privacy-source");
  privacySourceVideo = null;
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  cachedRects = [];
  if (overlay) {
    const ctx = overlay.getContext("2d");
    if (ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, overlay.width, overlay.height);
      ctx.restore();
    }
  }
}

/** ライブ終了時に WebGL メモリを解放する（再開時は ensure から再読込） */
export function disposeFacePrivacyModel() {
  blazeRef?.dispose();
  blazeRef = null;
  modelPromise = null;
}
